import { computed, watch, ref } from 'vue';

import getDefaultSectionDataConfig from '@/utils/getDefaultSectionDataConfig';
import getDefaultConfig from '@/utils/getDefaultConfig';
import formatValue from '@/utils/formatValue';
import getDiffClass from '@/utils/getDiffClass';

import {
  commonPipeConfig,
  summaryPropertyTotalPipe,
  scoreOverTimeMonthPipe,
  conversionTotalPipe,
} from '@/state';

import i18n from '@/config/i18n';

import {
  summaryDescriptions,
} from '@/config/infoDescriptions';
import {
  numericFormat,
  percentFormat,
  numericCurrFormat,
} from '@/config/valueFormatConfig';

import useFilterState from '@/state/useFilterState';
import useSectionState from '@/state/useSectionState';

const { t } = i18n.global;

const {
  sectionCharts,
} = useSectionState();

const {
  propertySummaryDaysSelected,
} = useFilterState();

const data = getDefaultSectionDataConfig([
  ...commonPipeConfig,
  // scores
  { key: 'dbiScoreMonth', pipe: scoreOverTimeMonthPipe },
  { key: 'conversionScoreMonth', pipe: scoreOverTimeMonthPipe },
  { key: 'bookingsScoreMonth', pipe: scoreOverTimeMonthPipe },
  { key: 'demandScoreMonth', pipe: scoreOverTimeMonthPipe },
  { key: 'revenueScoreMonth', pipe: scoreOverTimeMonthPipe },
  { key: 'ratesScoreMonth', pipe: scoreOverTimeMonthPipe },
  { key: 'disparitiesScoreMonth', pipe: scoreOverTimeMonthPipe },
  // funnel
  { key: 'beConversion', pipe: conversionTotalPipe },
  { key: 'searchConversion', pipe: conversionTotalPipe },
  // kpi
  { key: 'conversion', pipe: summaryPropertyTotalPipe },
  { key: 'bookings', pipe: summaryPropertyTotalPipe },
  { key: 'bestAvailableRate', pipe: summaryPropertyTotalPipe },
  { key: 'frequency', pipe: summaryPropertyTotalPipe },
]);

const metrics = [];

// Generate score configs
const scoreCharts = [
  {
    bigTitle: 'Direct Booking Index (DBI)',
    title: 'Direct Booking Index',
    uniqueDataKey: 'dbiScoreMonth',
    tooltipConfig: summaryDescriptions.charts.dbi,
  },
  {
    title: 'Conversion Index',
    uniqueDataKey: 'conversionScoreMonth',
  },
  {
    title: 'Bookings Index',
    uniqueDataKey: 'bookingsScoreMonth',
  },
  {
    title: 'Demand Index',
    uniqueDataKey: 'demandScoreMonth',
  },
  {
    title: 'Revenue Index',
    uniqueDataKey: 'revenueScoreMonth',
  },
  {
    title: 'Disparities Index',
    uniqueDataKey: 'disparitiesScoreMonth',
  },
].reduce((acc, curr) => {
  const baseConfig = {
    type: 'score',
    uniqueDataKey: curr.uniqueDataKey,
    dataFormat: numericFormat,
    tooltipConfig: curr.tooltipConfig,
  };

  // Big (selected) score chart
  acc.push({
    ...baseConfig,
    title: curr.bigTitle || curr.title,
    reportDisabled: true,
  });

  // Small score charts
  acc.push({
    ...baseConfig,
    title: curr.title,
    subType: 'small',
    reportWidth: 300,
  });

  return acc;
}, []);

const charts = getDefaultConfig([
  ...scoreCharts,
  {
    type: 'funnel_bar',
    subType: 'funnel',
    title: 'Visitor to BE',
    dataFormat: percentFormat,
    uniqueDataKey: 'searchConversion',
    tooltipConfig: summaryDescriptions.charts.searchConversion,
  },
  {
    type: 'funnel_bar',
    subType: 'funnel',
    title: 'BE to Booking',
    dataFormat: percentFormat,
    uniqueDataKey: 'beConversion',
    tooltipConfig: summaryDescriptions.charts.beConversion,
  },
  {
    type: 'kpi_bar',
    reportWidth: 500,
    subType: 'kpi',
    title: 'Total Conversion',
    dataFormat: percentFormat,
    uniqueDataKey: 'conversion',
    tooltipConfig: summaryDescriptions.charts.conversion,
    forceCtp: true,
  },
  {
    type: 'kpi_bar',
    reportWidth: 500,
    subType: 'kpi',
    title: 'Bookings',
    dataFormat: numericFormat,
    uniqueDataKey: 'bookings',
    tooltipConfig: summaryDescriptions.charts.bookings,
    forceCtp: true,
  },
  {
    type: 'kpi_bar',
    reportWidth: 500,
    subType: 'kpi',
    title: 'Best Available Rate',
    dataFormat: numericCurrFormat,
    uniqueDataKey: 'bestAvailableRate',
    tooltipConfig: summaryDescriptions.charts.rate,
    forceCtp: true,
  },
  {
    type: 'kpi_bar',
    reportWidth: 500,
    subType: 'kpi',
    title: 'Disparities',
    dataFormat: percentFormat,
    uniqueDataKey: 'frequency',
    tooltipConfig: summaryDescriptions.charts.frequency,
    forceCtp: true,
  },
], 'charts');

// loading flags
const kpiPipeDataLoading = computed(() => summaryPropertyTotalPipe.loading);
const conversionPipeLoading = computed(() => conversionTotalPipe.loading);
const allPipesLoaded = computed(() => !conversionTotalPipe.loading
  && !kpiPipeDataLoading.value
  && !scoreOverTimeMonthPipe.loading);

/*
  Chart aggrupation based on the inner sections of summary
  Used both for mounting and generating PDF reports
*/

// score charts
const smallScoreCharts = ref([]);
const fullScoreCharts = ref([]);
// funnel charts
const funnelCharts = computed(() => sectionCharts.value.filter((c) => c.subType === 'funnel'));
// kpi charts
const kpiCharts = computed(() => {
  if (kpiPipeDataLoading.value) {
    return [];
  }

  return sectionCharts.value
    .reduce((acc, curr) => {
      if (curr.subType === 'kpi') {
        const filteredOwnItem = curr.data.values.filter((v) => v.isOwn)[0];

        const newData = {
          ...curr,
        };

        if (filteredOwnItem) {
          const { value, diff } = filteredOwnItem;
          // useful data needed for rendering & reports
          Object.assign(newData, {
            ownValue: formatValue(value, curr.dataFormat),
            diff: formatValue(Math.abs(diff), '.0~%'),
            diffClass: `${getDiffClass(formatValue(diff, '.0~'))} ${curr.uniqueDataKey}`,
            diffText: propertySummaryDaysSelected.value > 1 ? t('vs previous {num} days', { num: propertySummaryDaysSelected.value }) : t('vs previous day'),
          });
        }

        acc.push(newData);
      }

      return acc;
    }, []);
});

// populate score items on load
watch(scoreOverTimeMonthPipe, () => {
  if (scoreOverTimeMonthPipe.loading) {
    return;
  }

  const scoreChartList = sectionCharts.value
    .reduce((acc, curr) => {
      if (curr.type === 'score') {
        const { value: lastMonthScore, diff } = [...curr.data.values]
          .sort((a, b) => b.date - a.date)[0] || {};

        // useful data needed for rendering & reports
        Object.assign(curr, {
          lastMonthScore,
          diff: Math.abs(diff),
          diffClass: getDiffClass(diff),
          diffText: t('vs previous month'),
        });

        if (curr.subType === 'small') {
          acc.smallScoreCharts.push(curr);
        } else {
          acc.fullScoreCharts.push(curr);
        }
      }

      return acc;
    }, {
      fullScoreCharts: [],
      smallScoreCharts: [],
    });

  smallScoreCharts.value = scoreChartList.smallScoreCharts;
  fullScoreCharts.value = scoreChartList.fullScoreCharts;
});

export default function useSummaryPropertyState() {
  return {
    charts,
    data,
    metrics,

    scorePipeData: scoreOverTimeMonthPipe,
    conversionPipeLoading,
    kpiPipeDataLoading,
    allPipesLoaded,

    smallScoreCharts,
    fullScoreCharts,
    funnelCharts,
    kpiCharts,
  };
}
